import React, { useState, useEffect, useRef } from "react";
import {
  getCustomerByPhone,
  getTransactions,
  getRewardTiers,
  getPromotion,
} from "../firebase";
import { AnimatePresence, motion } from "framer-motion";
import "../styles/CustomerKiosk.css";
import logoImg from "/Users/omaretkaidek/iCloud Drive (Archive)/Desktop/Work/cobra-loyalty-app/loyalty-points-app/src/logo.png";

const CustomerKiosk = () => {
  // Main states
  const [view, setView] = useState("welcome");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [rewardTiers, setRewardTiers] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [activePromotion, setActivePromotion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [appSettings, setAppSettings] = useState({
    pointsPerDollar: 10,
    minRedemptionPoints: 1000,
  });

  // Reset app after inactivity
  useEffect(() => {
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      setInactivityTimer(
        setTimeout(() => {
          resetApp();
        }, 180000)
      ); // 3 minutes of inactivity
    };

    // Set up initial timer
    resetTimer();

    // Reset timer on user interaction
    const handleUserActivity = () => resetTimer();
    window.addEventListener("touchstart", handleUserActivity);
    window.addEventListener("mousemove", handleUserActivity);

    return () => {
      clearTimeout(inactivityTimer);
      window.removeEventListener("touchstart", handleUserActivity);
      window.removeEventListener("mousemove", handleUserActivity);
    };
  }, [inactivityTimer]);

  // Initial data fetch
  useEffect(() => {
    const fetchRewardTiers = async () => {
      try {
        const tiers = await getRewardTiers();
        const sortedTiers = tiers
          .filter((tier) => tier.active)
          .sort((a, b) => a.pointsRequired - b.pointsRequired);
        setRewardTiers(sortedTiers);
      } catch (err) {
        console.error("Error fetching reward tiers:", err);
      }
    };

    const fetchPromotions = async () => {
      try {
        const allPromotions = await getPromotion();
        const now = new Date();

        // Filter for active promotions
        const validPromotions = allPromotions.filter((promo) => {
          if (!promo.active) return false;

          const startDate = new Date(promo.startDate);
          const endDate = new Date(promo.endDate);
          return now >= startDate && now <= endDate;
        });

        setPromotions(validPromotions);

        // Set the best promotion as active (highest multiplier)
        if (validPromotions.length > 0) {
          const bestPromo = validPromotions.reduce(
            (best, current) =>
              current.multiplier > best.multiplier ? current : best,
            validPromotions[0]
          );
          setActivePromotion(bestPromo);
        } else {
          setActivePromotion(null);
        }
      } catch (err) {
        console.error("Error fetching promotions:", err);
      }
    };

    fetchRewardTiers();
    fetchPromotions();
  }, []);

  // Reset app to welcome screen
  const resetApp = () => {
    setView("welcome");
    setPhoneNumber("");
    setCustomer(null);
    setTransactions([]);
    setError("");
  };

  // Phone number input handlers
  const handlePhoneInput = (num) => {
    if (phoneNumber.length < 10) {
      setPhoneNumber((prev) => prev + num);
    }
  };

  const handlePhoneDelete = () => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  };

  const handlePhoneClear = () => {
    setPhoneNumber("");
  };

  // Format phone number for display
  const formatPhoneNumber = (phone) => {
    if (phone.length === 0) return "";
    if (phone.length <= 4) return phone;
    return `${phone.slice(0, 4)}-${phone.slice(4)}`;
  };

  // Look up customer by phone
  const handleLookup = async () => {
    if (phoneNumber.length < 10) {
      setError("الرجاء إدخال رقم هاتف كامل");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const customerData = await getCustomerByPhone(phoneNumber);
      if (customerData) {
        setCustomer(customerData);
        const customerTransactions = await getTransactions(phoneNumber);
        setTransactions(customerTransactions);
        setView("dashboard");
      } else {
        setError("لم يتم العثور على عميل بهذا الرقم");
      }
    } catch (err) {
      console.error("Error looking up customer:", err);
      setError("خطأ في البحث عن العميل. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  // Format points with animation
  const PointsCounter = ({ points }) => {
    // Initialize count to the actual points value, not 0
    const [count, setCount] = useState(parseInt(points) || 0);

    useEffect(() => {
      // Parse points to ensure it's a number
      const targetValue = parseInt(points) || 0;

      // If points is 0 or very small, just set it directly without animation
      if (targetValue <= 10) {
        setCount(targetValue);
        return;
      }

      // Start from the current count, not from 0
      let start = count;
      const end = targetValue;
      const duration = 1000; // 1 second animation

      // Ensure increment is never 0 or negative
      const increment = Math.max(1, (end - start) / (duration / 16));

      const timer = setInterval(() => {
        // Update start for next iteration
        start += increment;

        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }, [points, count]); // Include count in dependencies

    return <span className="points-counter">{count.toLocaleString()}</span>;
  };

  // Find nearest reward tier
  const getNextRewardTier = () => {
    if (!customer || !rewardTiers.length) return null;

    const nextTiers = rewardTiers.filter(
      (tier) => tier.pointsRequired > customer.totalPoints
    );

    // With ascending order, the first unreached tier is the next one
    return nextTiers.length > 0 ? nextTiers[0] : null;
  };

  const nextReward = getNextRewardTier();

  // Calculate progress percentage to next reward
  const getProgressPercentage = () => {
    if (!customer || !nextReward) return 100;

    // Find the previous tier's points requirement or use 0 if this is the first tier
    // Since rewardTiers is now in descending order, we need to find the next lower tier
    const prevTierPoints = rewardTiers
      .filter((tier) => tier.pointsRequired < nextReward.pointsRequired)
      .reduce((min, tier) => Math.max(min, tier.pointsRequired), 0);

    const totalNeeded = nextReward.pointsRequired - prevTierPoints;
    const progress = customer.totalPoints - prevTierPoints;

    return Math.min(100, Math.max(0, (progress / totalNeeded) * 100));
  };

  const calculateExpirationDate = () => {
    if (!customer || !customer.firstTransactionDate) return null;

    // Get expiration months from settings or use default
    const expirationMonths = appSettings.expirationMonths || 12;

    // If expiration is disabled (0 months), don't show an expiration date
    if (expirationMonths === 0) return null;

    // Calculate expiration date
    const firstTransactionDate = customer.firstTransactionDate.toDate();
    const expirationDate = new Date(firstTransactionDate);
    expirationDate.setMonth(firstTransactionDate.getMonth() + expirationMonths);

    return expirationDate;
  };

  // Check if points will expire soon (within 30 days)
  const isExpiringSoon = () => {
    const expirationDate = calculateExpirationDate();
    if (!expirationDate) return false;

    const today = new Date();
    const daysUntilExpiration = Math.ceil(
      (expirationDate - today) / (1000 * 60 * 60 * 24)
    );

    return daysUntilExpiration <= 30;
  };

  // Calculate days until expiration for the countdown
  const getDaysUntilExpiration = () => {
    const expirationDate = calculateExpirationDate();
    if (!expirationDate) return null;

    const today = new Date();
    return Math.max(
      0,
      Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24))
    );
  };

  return (
    <div className="kiosk-container rtl-support">
      {/* Modified Branding Header - Logo on left, home button next to brand name */}
      <div className="kiosk-header">
        <div className="header-content">
          <div className="brand-section">
            <h1 className="kiosk-logo">
              <span>
                كوبرا<span className="logo-accent">بوينتس</span>
              </span>
            </h1>
            {view !== "welcome" && (
              <button onClick={resetApp} className="home-button">
                الرئيسية
              </button>
            )}
          </div>
          <img src={logoImg} alt="شعار" className="logo-image" />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="kiosk-alert error-alert">
          <span>{error}</span>
          <button onClick={() => setError("")} className="alert-close">
            ×
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <AnimatePresence mode="wait">
        {/* Welcome Screen */}
        {view === "welcome" && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="welcome-container"
          >
            <div className="text-center">
              <h2 className="welcome-title">برنامج الولاء والمكافآت</h2>
              <p className="welcome-subtitle">
                تحقق من نقاطك واكتشف المكافآت المتاحة
              </p>

              {activePromotion && (
                <div className="promotion-banner">
                  <span className="promotion-icon">🔥</span>
                  <div className="promotion-content">
                    <h3 className="promotion-title">عرض خاص!</h3>
                    <p className="promotion-info">
                      {activePromotion.name}: اكسب {activePromotion.multiplier}×
                      نقاط على جميع المشتريات!
                    </p>
                    <p className="promotion-period">
                      ساري حتى{" "}
                      {new Date(activePromotion.endDate).toLocaleDateString(
                        "en-GB"
                      )}
                    </p>
                  </div>
                </div>
              )}

              <button
                onClick={() => setView("lookup")}
                className="start-button"
              >
                استعلم عن رصيدك
              </button>
            </div>

            <div className="rewards-preview">
              {/* Display rewards in descending order (already sorted) */}
              {rewardTiers.slice(0, 5).map((tier) => (
                <div key={tier.id} className="reward-preview-item">
                  {tier.imageUrl ? (
                    <div className="reward-preview-image">
                      <img src={tier.imageUrl} alt={tier.name} />
                    </div>
                  ) : (
                    <div className="reward-preview-icon">
                      <span>{tier.emoji || "🎁"}</span>
                    </div>
                  )}
                  <h3 className="reward-preview-name">{tier.name}</h3>
                  <p className="reward-preview-points">
                    {tier.pointsRequired} نقطة
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Phone Lookup Screen */}
        {view === "lookup" && (
          <motion.div
            key="lookup"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="lookup-container"
          >
            <h2 className="welcome-title">أدخل رقم هاتفك</h2>

            <div className="keypad-container glass-card">
              <div className="keypad-display">
                {formatPhoneNumber(phoneNumber) || "رقم الهاتف"}
              </div>

              <div className="keypad-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <button
                    key={num}
                    onClick={() => handlePhoneInput(num.toString())}
                    className="keypad-button"
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={handlePhoneClear}
                  className="keypad-button clear"
                >
                  مسح
                </button>
                <button
                  onClick={() => handlePhoneInput("0")}
                  className="keypad-button"
                >
                  0
                </button>
                <button
                  onClick={handlePhoneDelete}
                  className="keypad-button delete"
                >
                  حذف
                </button>
              </div>

              <button
                onClick={handleLookup}
                disabled={loading || phoneNumber.length < 10}
                className="lookup-button"
              >
                {loading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  "الاستعلام عن النقاط"
                )}
              </button>
            </div>

            <p
              className="privacy-note text-center"
              style={{ color: "rgba(0, 0, 0, 0.7)", marginTop: "1rem" }}
            >
              يتم استخدام رقم هاتفك للاستعلام عن حساب الولاء الخاص بك.
              <br />
              نحترم خصوصيتك ولن نشارك معلوماتك.
            </p>
          </motion.div>
        )}

        {/* Customer Dashboard */}
        {view === "dashboard" && customer && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <div className="dashboard-grid">
              {/* Customer Info */}
              <div className="glass-card customer-info-card">
                <h2 className="customer-name">{customer.name}</h2>
                <p className="customer-phone">
                  {formatPhoneNumber(customer.id)}
                </p>
                <p className="customer-detail">
                  عضو منذ:{" "}
                  {customer.registrationDate
                    ?.toDate()
                    .toLocaleDateString("en-GB") || "غير متوفر"}
                </p>
                <p className="customer-detail">
                  إجمالي المعاملات: {customer.transactionCount || 0}
                </p>
              </div>

              {/* Points Display */}
              <div className="points-card">
                <div>
                  <h3 className="points-label">رصيد النقاط</h3>
                  <div className="points-value">
                    <PointsCounter points={customer.totalPoints || 0} />
                  </div>

                  {/* Expiration Information */}
                  {calculateExpirationDate() && (
                    <div
                      className={`expiration-info ${
                        isExpiringSoon() ? "expiring-soon" : ""
                      }`}
                    >
                      <div className="expiration-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </div>
                      <div className="expiration-details">
                        <span className="expiration-label">
                          تنتهي صلاحية النقاط في
                        </span>
                        <span className="expiration-date">
                          {calculateExpirationDate().toLocaleDateString(
                            "en-GB",
                            {
                              year: "numeric",
                              month: "numeric",
                              day: "numeric",
                            }
                          )}
                          {isExpiringSoon() && getDaysUntilExpiration() > 0 && (
                            <span className="expiration-countdown">
                              {" "}
                              (متبقي {getDaysUntilExpiration()} يوم)
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {nextReward ? (
                  <div className="progress-container">
                    <p className="progress-label">
                      <span>المكافأة التالية: {nextReward.name}</span>
                      <span>{nextReward.pointsRequired} نقطة</span>
                    </p>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${getProgressPercentage()}%` }}
                      />
                    </div>
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "rgba(255, 255, 255, 0.7)",
                        marginTop: "0.5rem",
                      }}
                    >
                      تحتاج إلى{" "}
                      {nextReward.pointsRequired - customer.totalPoints} نقطة
                      إضافية
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                      لقد وصلت إلى جميع مستويات المكافآت المتاحة!
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="dashboard-grid" style={{ marginTop: "1.5rem" }}>
              {/* Transaction History */}
              <div className="glass-card transactions-card">
                <h3 className="section-title">النشاط الأخير</h3>

                <div className="transactions-list">
                  {transactions.length > 0 ? (
                    <div>
                      {transactions.slice(0, 10).map((transaction) => (
                        <div key={transaction.id} className="transaction-item">
                          <div className="transaction-header">
                            <span className="transaction-date">
                              {transaction.timestamp
                                ?.toDate()
                                .toLocaleDateString("en-GB") || "حديثاً"}
                            </span>
                            <span
                              className={`transaction-points ${
                                transaction.points < 0
                                  ? "points-negative"
                                  : "points-positive"
                              }`}
                            >
                              {transaction.points > 0 ? "+" : ""}
                              {transaction.points} نقطة
                            </span>
                          </div>
                          <div className="transaction-detail">
                            {transaction.isRedemption
                              ? `استبدال: ${transaction.rewardName}`
                              : `عملية شراء: ₪${parseFloat(
                                  transaction.amount
                                ).toFixed(2)}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        color: "rgba(255, 255, 255, 0.5)",
                      }}
                    >
                      لا يوجد نشاط حديث
                    </p>
                  )}
                </div>
              </div>

              {/* Available Rewards - now displayed in descending order (already sorted) */}
              <div className="glass-card rewards-card">
                <h3 className="section-title">المكافآت المتاحة</h3>

                <div className="rewards-grid">
                  {rewardTiers.map((tier) => (
                    <div
                      key={tier.id}
                      className={`reward-item ${
                        customer.totalPoints >= tier.pointsRequired
                          ? "reward-available"
                          : "reward-locked"
                      }`}
                    >
                      <div className="reward-header">
                        {tier.imageUrl ? (
                          <div className="reward-image-container">
                            <img
                              src={tier.imageUrl}
                              alt={tier.name}
                              className="reward-image"
                            />
                          </div>
                        ) : (
                          <span className="reward-icon">
                            {tier.emoji || "🎁"}
                          </span>
                        )}
                        <h4 className="reward-name">{tier.name}</h4>
                      </div>
                      <p className="reward-description">{tier.description}</p>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span className="reward-points">
                          {tier.pointsRequired}
                        </span>
                        {customer.totalPoints >= tier.pointsRequired && (
                          <span className="reward-status">متاح</span>
                        )}
                      </div>

                      {customer.totalPoints < tier.pointsRequired && (
                        <div className="reward-overlay">
                          <div className="reward-needs">
                            تحتاج إلى{" "}
                            {tier.pointsRequired - customer.totalPoints} نقطة
                            إضافية
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Active Promotions */}
            {activePromotion && (
              <div className="promotion-banner" style={{ marginTop: "1.5rem" }}>
                <span className="promotion-icon">🔥</span>
                <div>
                  <h3 className="promotion-title">
                    عرض ترويجي نشط: {activePromotion.name}
                  </h3>
                  <p className="promotion-info">
                    اكسب {activePromotion.multiplier}× نقاط على جميع المشتريات
                    حتى{" "}
                    {new Date(activePromotion.endDate).toLocaleDateString(
                      "en-GB"
                    )}
                  </p>
                </div>
              </div>
            )}

            <div
              className="info-note"
              style={{
                textAlign: "center",
                padding: "1rem",
                marginTop: "1rem",
              }}
            >
              <p>لاستبدال نقاطك بمكافآت، يرجى التحدث مع أحد موظفي المتجر</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div className="kiosk-footer">
        © 2025 كوبرا بوينتس • هل تحتاج إلى مساعدة؟ يرجى الاستفسار من أحد أعضاء
        فريقنا
      </div>
    </div>
  );
};

export default CustomerKiosk;
